import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Row, Col, Card, Table, Form, Badge, Spinner, Alert, Button, Modal } from "react-bootstrap";
import Swal from "sweetalert2";
import PageLayout from "../../../../layouts/PageLayout";
import { apiGet, apiPut, errorText, rs, date } from "../../../Seller/sellerApi";

const STATUS_VARIANT = { Pending: "warning", "In Review": "info", Resolved: "success", Rejected: "secondary" };

export default function ComplaintsPage() {
    const [rows, setRows] = useState([]);
    const [statuses, setStatuses] = useState(["Pending", "In Review", "Resolved", "Rejected"]);
    const [filter, setFilter] = useState("open");
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [selected, setSelected] = useState(null);
    const [form, setForm] = useState({ status: "", admin_response: "" });
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const res = await apiGet("/admin/complaints", { status: filter || undefined, search: search || undefined });
            setRows(res.data?.data || []);
            if (res.data?.statuses) setStatuses(res.data.statuses);
        } catch (e) {
            setError(errorText(e, "Could not load complaints."));
        } finally {
            setLoading(false);
        }
    }, [filter, search]);

    useEffect(() => {
        const t = setTimeout(load, 300);
        return () => clearTimeout(t);
    }, [load]);

    const open = async (row) => {
        setSelected({ ...row, settlements: null });
        setForm({ status: row.status, admin_response: row.admin_response || "" });
        try {
            const res = await apiGet(`/admin/complaints/${row.id}`);
            setSelected(res.data?.data);
        } catch (e) {
            // keep the list data
        }
    };

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await apiPut(`/admin/complaints/${selected.id}`, form);
            setSelected(null);
            Swal.fire({ icon: "success", text: "Complaint updated. Seller payout holds were re-checked.", timer: 2200, showConfirmButton: false });
            load();
        } catch (err) {
            Swal.fire({ icon: "error", text: errorText(err) });
        } finally {
            setSaving(false);
        }
    };

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card mb-4">
                        <div className="mc-breadcrumb">
                            <h3 className="mc-breadcrumb-title">Complaints</h3>
                            <ul className="mc-breadcrumb-list">
                                <li className="mc-breadcrumb-item"><Link to="/dashboard" className="mc-breadcrumb-link">Home</Link></li>
                                <li className="mc-breadcrumb-item">Complaints</li>
                            </ul>
                        </div>
                    </div>
                </Col>
                <Col xl={12}>
                    <Card className="border-0 shadow-sm rounded-4">
                        <Card.Body className="p-4">
                            <p className="text-muted small">An open complaint holds the seller payout of that order until it is resolved or rejected.</p>
                            <Row className="g-2 mb-3">
                                <Col md={3}>
                                    <Form.Select value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Status">
                                        <option value="open">Open</option>
                                        <option value="">All</option>
                                        {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                                    </Form.Select>
                                </Col>
                                <Col md={5}>
                                    <Form.Control placeholder="Search subject, order or buyer" value={search} onChange={(e) => setSearch(e.target.value)} />
                                </Col>
                            </Row>
                            {error && <Alert variant="danger">{error}</Alert>}
                            {loading ? (
                                <div className="text-center py-5"><Spinner animation="border" /></div>
                            ) : rows.length === 0 ? (
                                <p className="text-muted text-center py-4 mb-0">No complaints.</p>
                            ) : (
                                <Table hover responsive className="align-middle mb-0">
                                    <thead>
                                        <tr><th>Date</th><th>Buyer</th><th>Order</th><th>Subject</th><th>Seller payout held</th><th>Status</th><th></th></tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((c) => (
                                            <tr key={c.id}>
                                                <td>{date(c.created_at)}</td>
                                                <td>{c.user?.name}<small className="d-block text-muted">{c.user?.email}</small></td>
                                                <td>{c.order?.order_number || "-"}</td>
                                                <td style={{ maxWidth: 280 }}>{c.subject}</td>
                                                <td>{c.held_items ? `${c.held_items} item(s) · ${rs(c.held_amount_pkr)}` : "-"}</td>
                                                <td><Badge bg={STATUS_VARIANT[c.status] || "secondary"}>{c.status}</Badge></td>
                                                <td className="text-end"><Button size="sm" variant="primary" onClick={() => open(c)}>Open</Button></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Modal show={!!selected} onHide={() => setSelected(null)} size="lg" centered>
                {selected && (
                    <Form onSubmit={save}>
                        <Modal.Header closeButton>
                            <Modal.Title>{selected.subject}</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <p className="small text-muted mb-1">
                                {selected.user?.name} · {selected.user?.email} · Order {selected.order?.order_number || "-"} ({selected.order?.payment_status}, {selected.order?.delivery_status})
                            </p>
                            <p style={{ whiteSpace: "pre-line" }}>{selected.message}</p>
                            <div className="d-flex gap-3 flex-wrap mb-3">
                                {selected.image && <a href={selected.image} target="_blank" rel="noreferrer"><img src={selected.image} alt="Complaint" style={{ maxHeight: 160 }} className="border rounded" /></a>}
                                {selected.video && <video src={selected.video} controls style={{ maxHeight: 200, maxWidth: "100%" }} />}
                            </div>
                            {Array.isArray(selected.settlements) && selected.settlements.length > 0 && (
                                <Alert variant="warning" className="small">
                                    Seller payouts on this order:{" "}
                                    {selected.settlements.map((s) => `${s.product_code || s.title}: ${rs(s.net_pkr)} (${s.status_label})`).join("; ")}
                                </Alert>
                            )}
                            <Form.Group className="mb-3">
                                <Form.Label>Status</Form.Label>
                                <Form.Select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                                    {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                                </Form.Select>
                            </Form.Group>
                            <Form.Group>
                                <Form.Label>Response to the buyer</Form.Label>
                                <Form.Control as="textarea" rows={4} value={form.admin_response} onChange={(e) => setForm({ ...form, admin_response: e.target.value })} />
                            </Form.Group>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => setSelected(null)}>Close</Button>
                            <Button type="submit" disabled={saving}>{saving ? "Saving..." : "Save"}</Button>
                        </Modal.Footer>
                    </Form>
                )}
            </Modal>
        </PageLayout>
    );
}
